<script setup>
/* global axios */
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  agentId: { type: String, default: null },
  apiBasePath: { type: String, required: true },
  i18nPrefix: { type: String, default: 'POWER_AGENTS.FORM' },
  accountId: { type: [String, Number], default: null },
});
const emit = defineEmits(['restore', 'close']);
const { t } = useI18n();
const route = useRoute();
function tp(suffix, params) {
  return t(`${props.i18nPrefix}.${suffix}`, params);
}

// Fallbacks kept so PowerAgents keeps working when explicit props are absent.
const resolvedAccountId = computed(
  () => props.accountId ?? route.params.accountId
);
const resolvedAgentId = computed(() => props.agentId);

const versions = ref([]);
const loading = ref(true);
const restoring = ref(null);
const deleting = ref(null);
const expandedVersion = ref(null);
const previewContent = ref('');
const previewLoading = ref(false);

async function fetchVersions() {
  loading.value = true;
  try {
    const { data } = await axios.get(`${props.apiBasePath}/prompt-versions`);
    versions.value = data.versions || [];
  } catch (e) {
    /* silent */
  } finally {
    loading.value = false;
  }
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return (
    d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) +
    ' ' +
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  );
}

async function toggleExpand(versionId) {
  if (expandedVersion.value === versionId) {
    expandedVersion.value = null;
    previewContent.value = '';
    return;
  }

  expandedVersion.value = versionId;
  previewLoading.value = true;
  previewContent.value = '';

  // Fetch full version content for preview
  try {
    const { data } = await axios.get(
      `${props.apiBasePath}/prompt-versions/${versionId}`
    );
    previewContent.value = data.system_prompt || '';
  } catch (e) {
    previewContent.value = '(erro ao carregar)';
  } finally {
    previewLoading.value = false;
  }
}

async function restoreVersion(version) {
  restoring.value = version.id;
  try {
    // The restore response carries the agent's now-current system_prompt; the list
    // item doesn't, so emit the real restored text or the editor would blank out.
    const { data } = await axios.post(
      `${props.apiBasePath}/prompt-versions/${version.id}/restore`
    );
    useAlert(tp('EDITOR_VERSION_RESTORED'));
    emit('restore', { ...version, system_prompt: data.system_prompt });
    expandedVersion.value = null;
    previewContent.value = '';
    await fetchVersions();
  } catch (e) {
    useAlert(tp('EDITOR_VERSION_ERROR'));
  } finally {
    restoring.value = null;
  }
}

async function deleteVersion(version) {
  deleting.value = version.id;
  try {
    await axios.delete(`${props.apiBasePath}/prompt-versions/${version.id}`);
    expandedVersion.value = null;
    previewContent.value = '';
    await fetchVersions();
  } catch (e) {
    useAlert('Erro ao deletar versão');
  } finally {
    deleting.value = null;
  }
}

onMounted(fetchVersions);
</script>

<template>
  <div
    class="flex flex-col max-h-[70vh]"
    :data-account-id="resolvedAccountId"
    :data-agent-id="resolvedAgentId"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-[#2d2d2d] flex-shrink-0"
    >
      <h3 class="text-sm font-medium text-[#d4d4d4]">
        {{ tp('VERSIONS_TITLE') }}
      </h3>
      <button
        class="text-[#6b6b6b] hover:text-[#d4d4d4] transition-colors p-1"
        @click="$emit('close')"
      >
        <i class="i-lucide-x text-base" />
      </button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto">
      <div v-if="loading" class="p-4 text-sm text-[#6b6b6b] text-center">
        {{ tp('VERSIONS_LOADING') }}
      </div>

      <div
        v-else-if="!versions.length"
        class="p-6 text-sm text-[#6b6b6b] text-center"
      >
        {{ tp('VERSIONS_EMPTY') }}
      </div>

      <div v-else class="flex flex-col">
        <div
          v-for="(v, i) in versions"
          :key="v.id"
          role="button"
          tabindex="0"
          class="text-left px-4 py-3 cursor-pointer hover:bg-[#252525] transition-colors border-b border-[#1f1f1f] group"
          @click="toggleExpand(v.id)"
          @keydown.enter="toggleExpand(v.id)"
          @keydown.space.prevent="toggleExpand(v.id)"
        >
          <div class="flex items-center justify-between">
            <span class="text-xs font-mono"
style="color: #ff13f0"
              >v{{ versions.length - i }}</span>
            <span class="text-[10px] text-[#5a5a5a]">{{
              formatDate(v.created_at)
            }}</span>
          </div>
          <p class="text-xs text-[#a0a0a0] mt-1 line-clamp-1">
            {{ v.description || '—' }}
          </p>
          <div class="flex items-center gap-3 mt-1 text-[10px] text-[#5a5a5a]">
            <span>{{
              tp('EDITOR_WORDS', {
                n: Math.round(v.prompt_length / 5),
              })
            }}</span>
            <span v-if="v.temperature != null">temp {{ v.temperature.toFixed(1) }}</span>
          </div>

          <!-- Expanded: preview + actions -->
          <div
            v-if="expandedVersion === v.id"
            class="mt-3 pt-3 border-t border-[#2d2d2d]"
          >
            <!-- Preview of prompt content -->
            <div v-if="previewLoading" class="text-xs text-[#5a5a5a] py-2">
              Carregando...
            </div>
            <div
              v-else
              class="bg-[#141414] rounded-md p-3 mb-3 max-h-40 overflow-y-auto"
            >
              <pre
                class="text-[11px] text-[#a0a0a0] font-mono whitespace-pre-wrap leading-relaxed"
                >{{ previewContent?.substring(0, 600)
                }}{{ previewContent?.length > 600 ? '...' : '' }}</pre>
            </div>

            <div class="flex gap-2">
              <button
                class="flex-1 text-xs py-1.5 px-3 rounded-md bg-[#252525] text-[#d4d4d4] hover:bg-[#2d2d2d] transition-colors border border-[#3d3d3d]"
                :disabled="restoring === v.id"
                @click.stop="restoreVersion(v)"
              >
                {{ restoring === v.id ? '...' : tp('VERSIONS_RESTORE') }}
              </button>
              <button
                class="text-xs py-1.5 px-2 rounded-md text-[#6b6b6b] hover:text-[#ef4444] hover:bg-[#2d1a1a] transition-colors"
                :disabled="deleting === v.id"
                title="Delete"
                @click.stop="deleteVersion(v)"
              >
                <i class="i-lucide-trash-2 text-sm" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
